// 记忆检索服务 - 检索和构建角色的长期记忆上下文
import enhancedStorageService from './enhancedStorageService.js'

class MemoryService {
  constructor() {
    this.db = null
  }

  /**
   * 确保数据库已初始化
   */
  async ensureDB() {
    if (!this.db) {
      this.db = await enhancedStorageService.ensureDB()
    }
    return this.db
  }

  /**
   * 检索相关历史摘要
   * @param {string} characterId - 角色ID
   * @param {Array} currentKeywords - 当前对话的关键词
   * @param {number} limit - 最多返回的摘要数量
   * @returns {Array} 相关摘要列表
   */
  async retrieveRelevantSummaries(characterId, currentKeywords = [], limit = 5) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['chatSummaries'], 'readonly')
      const store = transaction.objectStore('chatSummaries')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const allSummaries = request.result || []
          
          // 按相关性排序
          const scoredSummaries = allSummaries.map(summary => {
            let score = 0
            
            // 关键词匹配得分
            if (currentKeywords.length > 0) {
              const matchedKeywords = summary.keywords.filter(k => 
                currentKeywords.some(ck => k.includes(ck) || ck.includes(k))
              )
              score += matchedKeywords.length * 10
            }
            
            // 时间权重（最近的摘要权重更高）
            const daysAgo = (Date.now() - summary.timestamp) / (1000 * 60 * 60 * 24)
            score += Math.max(0, 30 - daysAgo) // 30天内权重递减
            
            return { ...summary, score }
          })
          
          // 按分数排序，取前N个
          const relevantSummaries = scoredSummaries
            .sort((a, b) => b.score - a.score)
            .slice(0, limit)
            .map(({ score, ...summary }) => summary) // 移除score字段
          
          resolve(relevantSummaries)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 检索历史摘要失败:', error)
      return []
    }
  }

  /**
   * 检索角色的长期记忆点
   * @param {string} characterId - 角色ID
   * @returns {Array} 记忆点列表
   */
  async retrieveCharacterMemories(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterMemories'], 'readonly')
      const store = transaction.objectStore('characterMemories')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const memories = (request.result || [])
            .sort((a, b) => b.importance - a.importance) // 按重要性排序
            .slice(0, 10) // 最多返回10个重要记忆
          resolve(memories)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 检索角色记忆失败:', error)
      return []
    }
  }

  /**
   * 构建记忆上下文文本
   * @param {string} characterId - 角色ID
   * @param {Array} currentKeywords - 当前对话关键词
   * @returns {string} 记忆上下文文本
   */
  async buildMemoryContext(characterId, currentKeywords = []) {
    const [summaries, memories] = await Promise.all([
      this.retrieveRelevantSummaries(characterId, currentKeywords, 3),
      this.retrieveCharacterMemories(characterId)
    ])

    const contextParts = []

    // 添加相关历史摘要
    if (summaries.length > 0) {
      contextParts.push('根据我们之前的聊天，你记得：')
      summaries.forEach((summary, index) => {
        contextParts.push(`${index + 1}. ${summary.summary}`)
      })
    }

    // 添加重要记忆点
    if (memories.length > 0) {
      contextParts.push('\n重要的记忆点：')
      memories.forEach((memory, index) => {
        contextParts.push(`${index + 1}. ${memory.content}`)
      })
    }

    return contextParts.join('\n')
  }

  /**
   * 保存重要记忆点
   * @param {string} characterId - 角色ID
   * @param {string} content - 记忆内容
   * @param {number} importance - 重要性（1-10）
   */
  async saveMemory(characterId, content, importance = 5) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterMemories'], 'readwrite')
      const store = transaction.objectStore('characterMemories')

      const memory = {
        id: `memory-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        characterId,
        content,
        importance,
        timestamp: Date.now(),
        createdAt: new Date().toISOString()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(memory)
        request.onsuccess = () => {
          console.log('✅ 记忆点已保存:', memory.id)
          resolve(memory)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存记忆点失败:', error)
      throw error
    }
  }

  /**
   * 清理旧摘要（保留最近30天）
   */
  async cleanupOldSummaries(characterId, daysToKeep = 30) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['chatSummaries'], 'readwrite')
      const store = transaction.objectStore('chatSummaries')
      const index = store.index('characterId')

      const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000)

      return new Promise((resolve, reject) => {
        const request = index.openCursor(characterId)
        request.onsuccess = (event) => {
          const cursor = event.target.result
          if (cursor) {
            if (cursor.value.timestamp < cutoffTime) {
              cursor.delete()
            }
            cursor.continue()
          } else {
            console.log('✅ 旧摘要清理完成')
            resolve()
          }
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 清理旧摘要失败:', error)
    }
  }
}

export default new MemoryService()

